import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Copy, Plus, LogOut } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { API, errMsg, waLink } from "@/lib/api";
import { QuickReplies } from "@/components/admin/QuickReplies";
import { SalesMessages } from "@/components/admin/SalesMessages";
import { PendingSales } from "@/components/admin/PendingSales";
import { BrandDownloads } from "@/components/admin/BrandDownloads";
import { AffiliateAdmin } from "@/components/admin/AffiliateAdmin";
import { Logo, LogoMark } from "@/components/Logo";

const TOKEN_KEY = "alania_admin";
const productInfo = (productId) => productId === "rabietas-2-3"
  ? { name: "Rabietas: acompañar con límites", days: 7, supportDays: 0 }
  : { name: "Pack Equipo en Calma", days: 21, supportDays: 21 };

const codeMsg = (c) => {
  const product = productInfo(c.product_id);
  const support = product.supportDays ? ` Durante los ${product.supportDays} días del reto puedes escribirme por WhatsApp; te respondo en un máximo de 48 h laborables.` : "";
  return `¡Hola ${c.buyer}! Gracias por tu compra de ${product.name}. Tu código es: ${c.code}\nEntra aquí: ${window.location.origin}/acceso\nTu código sirve en un máximo de 2 dispositivos. Tienes ${product.days} días de contenido para avanzar a tu ritmo.${support}`;
};

const Login = ({ onToken }) => {
  const [pw, setPw] = useState("");
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password: pw });
      localStorage.setItem(TOKEN_KEY, data.token);
      onToken(data.token);
    } catch (err) { setError(errMsg(err)); }
  };
  return (
    <main className="min-h-screen grid place-items-center px-5">
      <form onSubmit={submit} className="w-full max-w-sm bg-white rounded-[2rem] border-2 border-[var(--ink)] p-8" data-testid="admin-login-form">
        <div className="text-2xl mb-4"><Logo /></div>
        <h1 className="font-display text-3xl">Panel privado</h1>
        <input data-testid="admin-password-input" type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Contraseña"
          className="w-full mt-6 rounded-2xl border-2 border-[#d9cbb8] focus:border-[var(--ink)] outline-none px-4 py-3" required />
        {error && <p data-testid="admin-login-error" className="text-sm text-[#c0392b] mt-3">{error}</p>}
        <button data-testid="admin-login-button" className="btn-accent w-full mt-5">Entrar</button>
      </form>
    </main>
  );
};

const Row = ({ c, onToggle, onReset }) => {
  const product = productInfo(c.product_id);
  const next = Array.from({ length: product.days }, (_, i) => i + 1).find((d) => !c.completed.includes(d));
  const nDev = (c.devices || []).length;
  return (
    <tr className="border-b border-[#eadfce]" data-testid={`code-row-${c.code}`}>
      <td className="py-3 pr-3 font-semibold tracking-wider">{c.code}</td>
      <td className="py-3 pr-3">{c.buyer}</td>
      <td className="py-3 pr-3 text-sm">{product.name}</td>
      <td className="py-3 pr-3 text-sm">{c.first_used_at ? product.supportDays ? `Usado · dudas hasta ${new Date(new Date(c.first_used_at).getTime() + product.supportDays * 864e5).toLocaleDateString("es-ES")}` : "Usado" : "Sin usar"}</td>
      <td className="py-3 pr-3 text-sm">{c.completed.length}/{product.days}{next && c.completed.length ? ` · va por el día ${next}` : ""}</td>
      <td className="py-3 pr-3 text-sm whitespace-nowrap">
        <span data-testid={`devices-count-${c.code}`} className={nDev >= 2 ? "font-semibold text-[var(--terra)]" : ""}>{nDev}/2</span>
        {nDev > 0 && <button data-testid={`reset-devices-${c.code}`} onClick={() => onReset(c.code)} className="ml-2 underline hover:text-[var(--accent)]">Liberar</button>}
      </td>
      <td className="py-3 pr-3"><Switch data-testid={`code-active-switch-${c.code}`} checked={c.active} onCheckedChange={(v) => onToggle(c.code, v)} /></td>
      <td className="py-3 whitespace-nowrap">
        <button data-testid={`copy-code-${c.code}`} onClick={() => { navigator.clipboard?.writeText(codeMsg(c)); toast.success("Mensaje copiado"); }} className="p-2 hover:text-[var(--accent)]" title="Copiar mensaje"><Copy size={16} /></button>
      </td>
    </tr>
  );
};

const AUTO_REPLY = "¡Hola! Me ha llegado tu mensaje. Gracias por escribirme 💛 Te respondo personalmente en un máximo de 48 horas laborables (de lunes a viernes). Mientras tanto, puedes seguir con el reto a tu ritmo.";

const AutoReplyCard = () => (
  <section data-testid="auto-reply-card" className="mt-8 rounded-2xl border-2 border-dashed border-[var(--sage)] p-5 sm:p-6">
    <p className="font-display text-xl">Respuesta automática para dudas (mensaje de ausencia)</p>
    <p className="mt-3 rounded-xl bg-[#DCF3D4] p-4 text-sm" data-testid="auto-reply-text">{AUTO_REPLY}</p>
    <button data-testid="copy-auto-reply-button" onClick={() => { navigator.clipboard?.writeText(AUTO_REPLY); toast.success("Respuesta copiada"); }} className="btn-ghost mt-4"><Copy size={16} /> Copiar mensaje</button>
    <ol className="mt-4 text-sm text-[#5a4f46] list-decimal pl-5 space-y-1">
      <li>Abre la app WhatsApp Business (es gratis; puedes pasar tu número a ella sin perder los chats).</li>
      <li>Ve a Ajustes → Herramientas para la empresa → Mensaje de ausencia.</li>
      <li>Actívalo, pega este texto y elige "Enviar siempre" (o solo fuera de tu horario).</li>
    </ol>
  </section>
);

export default function Admin() {
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY));
  const [codes, setCodes] = useState([]);
  const [buyer, setBuyer] = useState("");
    const [productId, setProductId] = useState("equipo-calma");
  const [last, setLast] = useState(null);
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const logout = () => { localStorage.removeItem(TOKEN_KEY); setToken(null); };
  const load = useCallback(() => {
    axios.get(`${API}/admin/codes`, { headers: { Authorization: `Bearer ${token}` } }).then((r) => setCodes(r.data)).catch(() => { localStorage.removeItem(TOKEN_KEY); setToken(null); });
  }, [token]);
  useEffect(() => { if (token) load(); }, [token, load]);

  if (!token) return <Login onToken={setToken} />;

  const create = async (e) => {
    e.preventDefault();
    try {
      const { data } = await axios.post(`${API}/admin/codes`, { buyer, product_id: productId }, auth);
      setLast(data); setBuyer(""); load();
    } catch (err) { toast.error(errMsg(err)); }
  };
  const toggle = async (code, active) => {
    try { await axios.patch(`${API}/admin/codes/${code}`, { active }, auth); load(); } catch (err) { toast.error(errMsg(err)); }
  };
  const resetDevices = async (code) => {
    if (!window.confirm(`¿Liberar los dispositivos de ${code}? Esa persona tendrá que volver a escribir su código.`)) return;
    try { await axios.post(`${API}/admin/codes/${code}/reset-devices`, {}, auth); toast.success("Dispositivos liberados"); load(); } catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <main data-testid="admin-page" className="px-5 sm:px-8 py-8 max-w-5xl mx-auto">
      <header className="flex justify-between items-center">
        <h1 className="font-display text-3xl inline-flex items-center gap-2"><LogoMark size={34} /> Tu panel</h1>
        <button data-testid="admin-logout-button" onClick={logout} className="text-sm inline-flex items-center gap-1.5"><LogOut size={16} /> Salir</button>
      </header>
      <PendingSales token={token} codeMessage={codeMsg} onCodeCreated={(c) => { setLast(c); load(); setTimeout(() => document.querySelector("[data-testid=new-code-result]")?.scrollIntoView({ behavior: "smooth", block: "center" }), 150); }} />
      <h2 className="font-display text-2xl mt-10">Códigos de acceso</h2>
      <p className="mt-1 text-sm text-[#5a4f46]">Cada código desbloquea únicamente el taller indicado en la columna “Taller”. Para que una persona acceda a ambos, crea un código para cada producto.</p>
      <form onSubmit={create} className="mt-4 flex flex-col sm:flex-row gap-3">
        <input data-testid="new-code-buyer-input" value={buyer} onChange={(e) => setBuyer(e.target.value)} placeholder="Nombre o teléfono del comprador" required
          className="flex-1 rounded-2xl border-2 border-[#d9cbb8] focus:border-[var(--ink)] outline-none px-4 py-3 bg-white" />
        <select data-testid="new-code-product-select" value={productId} onChange={(e) => setProductId(e.target.value)} className="rounded-2xl border-2 border-[#d9cbb8] bg-white px-3 py-3">
          <option value="equipo-calma">Equipo en Calma · 39 €</option>
          <option value="rabietas-2-3">Rabietas · 19 €</option>
        </select>
        <button data-testid="create-code-button" className="btn-accent"><Plus size={18} /> Crear código</button>
      </form>
      {last && (
        <div data-testid="new-code-result" className="mt-5 rounded-2xl bg-[var(--butter)] p-5 flex flex-col sm:flex-row sm:items-center gap-3">
          <p className="flex-1">Código para <b>{last.buyer}</b> · {productInfo(last.product_id).name}: <span className="font-display text-2xl" data-testid="new-code-value">{last.code}</span></p>
          <a data-testid="send-code-whatsapp" href={waLink(codeMsg(last)).replace(/wa\.me\/\d+/, "wa.me/")} target="_blank" rel="noreferrer" className="btn-ghost">Enviar por WhatsApp</a>
        </div>
      )}
      <div className="mt-8 overflow-x-auto bg-white rounded-2xl p-4">
        <table className="w-full text-left min-w-[760px]" data-testid="codes-table">
          <thead><tr className="text-xs uppercase tracking-wider text-[#7a6c5e]">{["Código", "Comprador", "Taller", "Estado", "Progreso", "Dispositivos", "Activo", ""].map((h) => <th key={h} className="pb-2">{h}</th>)}</tr></thead>
          <tbody>{codes.map((c) => <Row key={c.code} c={c} onToggle={toggle} onReset={resetDevices} />)}</tbody>
        </table>
        {!codes.length && <p className="text-sm text-[#7a6c5e] py-4">Aún no hay códigos.</p>}
      </div>
      <SalesMessages token={token} />
      <AutoReplyCard />
      <QuickReplies token={token} />
      <BrandDownloads />
      <AffiliateAdmin token={token} />
    </main>
  );
}
